import React, { useState, FC } from 'react';

import { Button, Grid, Container, Dialog, DialogContent, DialogActions } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete';
import DynamicFeedIcon from '@mui/icons-material/DynamicFeed';
import CreateIcon from '@mui/icons-material/Create';
import EmailIcon from '@mui/icons-material/Email';
import SaveIcon from '@mui/icons-material/Save';
import { gql, useMutation, useQuery } from '@apollo/client';
import * as types from "../gql/graphql";
import Card from "@mui/material/Card";
import MenuItem from '@mui/material/MenuItem';

import { SubmitErrorHandler, SubmitHandler, useForm, Controller, useWatch } from 'react-hook-form';
import { Calendar } from 'primereact/calendar';
import { classNames } from 'primereact/utils';

import MDBoxRoot from './MDBox/MDBoxRoot';
import MDButtonRoot from './MDButton/MDButtonRoot';
import MDInput from './MDInput/MDInputRoot';
import MDTypographyRoot from './MDTypography/MDTypographyRoot';
import CloseIcon from '@mui/icons-material/Close';

import { useScreenSize } from "../hooks/ScreenSizeContext";

import CreateSeriesDialog, { CreateSeriesFormData } from './CreateSeriesDialog';
import { SchoolTime } from '../utils/SchoolTime';
import { useQueryAll } from '../apolloClient';

type EventFormType = {
    capacity?: types.Maybe<types.Scalars["Int"]["output"]>;
    class?: types.Maybe<types.Scalars["BigInt"]["output"]>;
    date?: types.Maybe<types.Scalars["Datetime"]["output"]>;
    id: types.Scalars["BigInt"]["output"];
    notes?: types.Maybe<types.Scalars["String"]["output"]>;
    status?: types.Maybe<types.Scalars["Int"]["output"]>;
    day: Date;
    time: Date;
}

interface EditEventDialogProps {
    event: types.Fs_Events;
    onSave: () => void;
    onDelete: () => void;
    attendies: types.Fs_SignupEdge[];
    eventName: string;
    theme?: any;
}


// TODO: Reconcile Form with Dialog, is Diolog needed? 
const EditEventDialog: FC<EditEventDialogProps> = ({ event, onSave, onDelete, attendies, eventName, theme }) => {
    //type EventFormType = types.Fs_Events & { day: Date, time: Date };
    const isSmallScreen = useScreenSize();
    const expandedEvent: EventFormType = {
        ...event,
        day: SchoolTime.fromISO(event?.date)?.toJSDate(),
        time: SchoolTime.fromISO(event?.date)?.toJSDate()
    }

    const {
        register,
        control,
        handleSubmit,
        setValue,
        watch,
        trigger,
        formState: { errors } } = useForm<EventFormType>({
            mode: 'onChange',
            defaultValues: expandedEvent
        });


    //const [currEvent, setCurrEvent] = useState(event);
    const status = useWatch({ control, name: 'status', defaultValue: event?.status || 0 });
    const classValue = useWatch({ control, name: 'class', defaultValue: event?.class || '' });

    const [isSeriesOpen, setIsSeriesOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

    const saveEventDataQuery = gql(`
    mutation saveEventDataQuery($ev: fs_eventsUpdateInput!, $filter:fs_eventsFilter) {
        updatefs_eventsCollection(set: $ev, filter: $filter) {
            affectedCount
            records {
                nodeId
                id
                class
                fs_classes { name }
                capacity
                date
                status  
                replicated_from
            }
        }
    }
    `);

    const [saveEventMutation, { data, loading, error, reset: saveReset }] = useMutation(saveEventDataQuery, {
        refetchQueries: [
            // Query name
        ],
    });
    // if (data) {
    //     onSave(data?.updatefs_eventsCollection.records[0]);
    //     saveReset();
    // }

    const moveWLQuery = gql(`
    mutation mwl($id:BigInt!) {
        move_waitlist(eid:$id)
    }
    `);

    const [moveWLMuation, { data: mwl_data, loading: mwl_loading, error: mwl_error }] = useMutation(moveWLQuery);

    const deleteEventQuery = gql(`
    mutation deleteEventsQuery($filter:fs_eventsFilter) {
        deleteFromfs_eventsCollection(filter: $filter) {
            affectedCount
        }
    }
    `);

    const [deleteEventMutation, { data: del_data, loading: del_loading, error: del_error }] = useMutation(deleteEventQuery, {
        refetchQueries: [
            //'GetClasses' // Query name
        ],
    });

    const handleDeleteEvent = async () => {
        if (event.id == -1) {
            setIsDeleteDialogOpen(false);
            onDelete();
            return;
        }

        try {
            await deleteEventMutation({ variables: { filter: { id: { eq: event.id } } } });
        } catch (error) {
            console.error("Failed to delete event", error);
        }
        setIsDeleteDialogOpen(false);
        onDelete();
    };

    const handleDeleteClick = () => {
        setIsDeleteDialogOpen(true);
    }

    // TODO: Data has all row fields, which is more than is defined in the input for saving - types.Fs_EventsUpdateInput
    // Now filtering extra fields here. Need to find a better way to do that.   
    function pickObjectKeys<T, K extends keyof T>(obj: T, keys: K[]) {
        let result = {} as Pick<T, K>
        for (const key of keys) {
            if (obj.hasOwnProperty(key)) {
                result[key] = obj[key]
            }
        }
        return result
    }

    const handleEmailClick = () => {
        // opens Gmail with subject and list of student emails from attendies
        const subject = `Information about your upcoming class: ${eventName}`;
        const recipientEmails = attendies
            .filter((signup) => signup.node.status === 0) // status 1 = signed up
            .map((signup) => signup.node.fs_registrations.fs_purchases.fs_students)
            .filter((student): student is types.Fs_Students => student !== null && student !== undefined)
            .map((student) => student.Email)
            .join(',');
        const mailto = `mailto:?bcc=${encodeURIComponent(recipientEmails)}&subject=${encodeURIComponent(subject)}`;
        const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&bcc=${encodeURIComponent(recipientEmails)}&su=${encodeURIComponent(subject)}`;

        const opened = window.open(gmailUrl, '_blank');
        if (!opened) {
            // fallback to native mail client if popup was blocked
            window.location.href = mailto;
        }
    }

    const handleCreateSeriesClick = async () => {
        const isValid = await trigger();
        if (isValid) setIsSeriesOpen(true);
    }

    const handleSeriesSave = async (data: CreateSeriesFormData) => {

        const values = watch();

        values.day.setHours(values.time.getHours());
        values.day.setMinutes(values.time.getMinutes());
        values.day.setSeconds(values.time.getSeconds());

        const startDate = SchoolTime.fromJSDate(values.day);
        var lastDate: SchoolTime;

        if (data.repeatType === 'weeks') {
            lastDate = SchoolTime.fromJSDate(values.day);
            // Get the current day of the week (0 = Sunday, 6 = Saturday)
            const dayOfWeek = lastDate.getDayOfWeek();
            // Calculate how many days until the next Saturday
            const daysUntilSaturday = 6 - dayOfWeek;
            // Move to the first Saturday after startDate
            lastDate = lastDate.addDays(daysUntilSaturday);
            // Move forward numWeeks * 7 days
            lastDate = lastDate.addDays(data.numWeeks * 7);
        }
        else {
            lastDate = SchoolTime.fromISO(data.endDate + 'T00:00:00').addDays(1);
        }

        const generatedDates: string[] = [];
        let currentDate = startDate.addDays(1);

        while (currentDate.isSameOrBefore(lastDate)) {
            if (data.selectedDays[currentDate.getDayOfWeek()]) {
                generatedDates.push(currentDate.toISOLocal());
            }
            currentDate = currentDate.addDays(1);
        }
        try {
            await Promise.all(
                generatedDates.map(async (date) => {
                    await addEventMutation({
                        variables: {
                            ev: {
                                capacity: values.capacity,
                                class: values.class,
                                date,
                                status: values.status,
                                replicated_from: event.id
                            }
                        }
                    });
                })
            );
        } catch (error) {
            console.error("Failed to save events", error);
        }



        onSave();
    }

    const addEventDataQuery = gql(`
    mutation addEventDataQuery($ev: fs_eventsInsertInput!) {
        insertIntofs_eventsCollection(objects: [$ev]) {
            affectedCount
            records {
                nodeId
                id
                class
                fs_classes { name }
                capacity
                date
                status
                replicated_from  
            }            
        }
    }       
    `);

    const [addEventMutation, { data: add_data, loading: add_loading, error: add_error, reset: addReset }] = useMutation(addEventDataQuery, {
        refetchQueries: [
        ],
    });

    // if (add_data) {
    //     onSave(add_data?.insertIntofs_eventsCollection.records[0]);
    //     addReset();
    // }

    const getClassesForSelectionQuery = gql(`
    query GetClassesForEvent($after: Cursor) {
      fs_classesCollection(after: $after,
        filter: {active:{eq: true}}, 
        orderBy: [{category: AscNullsLast}, {listorder:AscNullsLast}] 
        ) {
          pageInfo { endCursor startCursor hasNextPage }
          edges {
            node {
              nodeId
              id
              name
            }
          }
        }
    }
    `);

    const { data: Cdata } = useQueryAll<types.Fs_ClassesEdge>({ query: getClassesForSelectionQuery });

    const submit: SubmitHandler<EventFormType> = async data => {


        // TODO: provide default value for listorder. Where?
        const filtered_data = pickObjectKeys(data, ['capacity', 'class', 'date', 'notes', 'status']);
        data.day.setHours(data.time.getHours());
        data.day.setMinutes(data.time.getMinutes());
        data.day.setSeconds(data.time.getSeconds());
        filtered_data.date = SchoolTime.fromJSDate(data.day).toISOLocal();

        try {
            if (event.id == -1) {
                await addEventMutation({ variables: { ev: filtered_data } });
            }
            else {
                await saveEventMutation({ variables: { filter: { nodeId: { eq: event.nodeId } }, ev: filtered_data } });
                await moveWLMuation({ variables: { id: event.id } });
            }
        } catch (error) {
            console.error("Failed to save event", error);
        }
        onSave();
    }



    const errorHandler: SubmitErrorHandler<types.Fs_EventsUpdateInput> = data => {
        console.log(data + ' 2!Error');
    }

    if (!event) return null;

    const getFormErrorMessage = (name) => {
        return errors[name] ? <small className="p-error">{errors[name].message}</small> : <small className="p-error">&nbsp;</small>;
    };

    const handleStatusChange = (e) => {
        setValue('status', e.target.value);
    };

    const handleClassChange = (e) => {
        setValue('class', e.target.value);
    };
    const inputStyle: React.CSSProperties = {
        width: '100%',
        padding: '4px 4px 5px',
        border: '0px solid #ccc',
        borderRadius: '4px',
        textAlign: 'left',
        boxSizing: 'content-box',
        background: 'none',
        height: '1.4375em',
        animationName: 'mui-auto-fill-cancel',
        animationDuration: '10ms',
        fontSize: "0.875rem",
        color: "#344767",
        backgroundColor: "#f5f5f5",
    };

    const handleInput = (e) => {
        const input = e.target;
        const originalValue = input.value;
        let start = input.selectionStart;
        let end = input.selectionEnd;

        let value = originalValue.toUpperCase();

        // 1. Convert spaces to colons (e.g., "3 00" -> "3:00")
        value = value.replace(/^(\d{1,2})\s+(\d{2})/, '$1:$2');
        value = value.replace(/^(\d{1,2})\s*([AP]M)/, '$1:00 $2');

        // 2. Pad single-digit hours with a leading zero (e.g., "3:00" -> "03:00")
        const match = value.match(/^(\d):(\d{2})(.*)$/);
        if (match) {
            value = `0${match[1]}:${match[2]}${match[3]}`;
            
            // If cursor was after the inserted '0', shift it right by 1
            if (start > 0) {
                start += 1;
                end += 1;
            }
        }

        // 3. Ensure a space exists before PM/AM (e.g., "03:00PM" -> "03:00 PM")
        const dynamicSpaceMatch = value.match(/(\d{2})([AP]M)$/);
        if (dynamicSpaceMatch) {
            const spaceIndex = value.length - 2;
            value = value.replace(/(\d{2})([AP]M)$/, '$1 $2');
            
            // If the cursor was exactly where the space was inserted, shift it right
            if (start > spaceIndex) {
                start += 1;
                end += 1;
            }
        }

        // Apply changes and handle cursor tracking safely
        if (originalValue !== value) {
            input.value = value;
            
            // Apply the mathematically corrected cursor positions
            input.setSelectionRange(start, end);

            // Notify PrimeReact to immediately parse the value
            const event = new Event('input', { bubbles: true });
            input.dispatchEvent(event);
        }
    };

    return (
        <MDBoxRoot>
            <Card sx={{
                ...(isSmallScreen
                    ? { padding: "4px 0px 8px" }
                    : { padding: "4px 16px 8px" }),
                minWidth: '-webkit-fill-available'
            }}>
                <MDBoxRoot ownerState={{
                    variant: "contained",
                    bgColor: "secondary",
                    coloredShadow: "secondary"
                }}
                    borderRadius={2}
                    sx={{ display: 'flex', width: '100%' }} pb={1} px={isSmallScreen ? 0 : 2} mt={-1} >
                    <MDTypographyRoot variant="h3" ownerState={{ color: "white" }} sx={isSmallScreen ? { padding: "0px 8px" } : { padding: "0" }}>
                        Edit Event
                    </MDTypographyRoot>
                </MDBoxRoot>
                <MDBoxRoot
                    component="main" px={isSmallScreen ? 0 : 2} py={4}
                    display="flex"
                    justifyContent="space-between"
                    sx={{ flexGrow: 1, width: '100%', textAlign: "center" }}
                >
                    {isSmallScreen ? (
                        <form onSubmit={handleSubmit(submit, errorHandler)} id="eventForm">
                            <MDBoxRoot
                                display="flex"
                                justifyContent="space-between"
                                flexDirection='column'
                                sx={{ width: '100%' }}
                            >
                                <MDBoxRoot component="table" width="100%" textAlign="left">
                                    <MDBoxRoot component="tbody">
                                        <MDBoxRoot
                                            lineHeight={1}
                                            component="tr"
                                            sx={{ verticalAlign: "middle" }}>
                                            <MDBoxRoot px={1} component="td" {...{ colSpan: 2 }}>
                                                <MDTypographyRoot variant="h5" px={1} ownerState={{ color: "dark" }}>Class</MDTypographyRoot>
                                            </MDBoxRoot>
                                        </MDBoxRoot>

                                        <MDBoxRoot
                                            lineHeight={1}
                                            component="tr"
                                            sx={{ verticalAlign: "middle" }}>
                                            <MDBoxRoot component="td" {...{ colSpan: 2 }}>
                                                <MDInput
                                                    {...register('class', { valueAsNumber: true })}
                                                    value={classValue}
                                                    select
                                                    label=""
                                                    variant="standard"
                                                    fullWidth
                                                    onChange={handleClassChange}>
                                                    {Cdata?.map((item) => { return <MenuItem value={item.node.id} key={item.node.id}>{item.node.name}</MenuItem> })}
                                                </MDInput>
                                                {errors.class && <MDBoxRoot><MDTypographyRoot variant="h6" px={1} ownerState={{ color: "error" }}>This field is required</MDTypographyRoot></MDBoxRoot>}
                                            </MDBoxRoot>
                                        </MDBoxRoot>

                                        <MDBoxRoot
                                            lineHeight={1}
                                            component="tr"
                                            sx={{ verticalAlign: "middle" }}>
                                            <MDBoxRoot px={1} component="td">
                                                <MDTypographyRoot variant="h5" px={1} ownerState={{ color: "dark" }}>Capacity</MDTypographyRoot>
                                            </MDBoxRoot>
                                            <MDBoxRoot sx={{ textAlign: "left" }} component="td" >
                                                <MDInput
                                                    {...register('capacity', { valueAsNumber: true, required: true })}
                                                    type='number'
                                                    label=""
                                                    variant="standard"
                                                    aria-invalid={errors.capacity ? true : false}
                                                    sx={{ marginLeft: '-10px' }} />
                                                {errors.capacity && <MDBoxRoot><MDTypographyRoot variant="h6" ownerState={{ color: "error" }}>This field is required</MDTypographyRoot></MDBoxRoot>}
                                            </MDBoxRoot>
                                        </MDBoxRoot>

                                        <MDBoxRoot
                                            lineHeight={1}
                                            component="tr"
                                            sx={{ verticalAlign: "middle" }}>
                                            <MDBoxRoot px={1} component="td">
                                                <MDTypographyRoot variant="h5" px={1} ownerState={{ color: "dark" }}>Notes</MDTypographyRoot>
                                            </MDBoxRoot>
                                            <MDBoxRoot component="td" >
                                                <MDInput
                                                    {...register('notes')}
                                                    label=""
                                                    variant="standard"
                                                    sx={{ width: "100%", textAlign: "left" }} />
                                            </MDBoxRoot>
                                        </MDBoxRoot>
                                    </MDBoxRoot>
                                </MDBoxRoot>
                                <MDBoxRoot component="table" width="100%" textAlign="left">
                                    <MDBoxRoot component="tbody">
                                        <MDBoxRoot
                                            lineHeight={1}
                                            component="tr"
                                            sx={{ verticalAlign: "middle" }}>
                                            <MDBoxRoot px={2} component="td">
                                                <MDTypographyRoot variant="h5"
                                                    ownerState={{ color: "dark" }}
                                                >
                                                    Status</MDTypographyRoot>
                                            </MDBoxRoot>
                                            <MDBoxRoot px={1} sx={{ width: "100%", textAlign: "left" }} component="td" >
                                                <MDInput
                                                    {...register('status', { valueAsNumber: true })}
                                                    value={status}
                                                    select
                                                    label=""
                                                    variant="standard"
                                                    fullWidth
                                                    onChange={handleStatusChange}
                                                >
                                                    <MenuItem value='0'>Scheduled</MenuItem>
                                                    <MenuItem value='1'>Completed</MenuItem>
                                                    <MenuItem value='2'>Canceled</MenuItem>
                                                </MDInput>
                                            </MDBoxRoot>
                                        </MDBoxRoot>
                                        <MDBoxRoot
                                            lineHeight={1}
                                            component="tr"
                                            sx={{ verticalAlign: "middle" }}>

                                            <Controller
                                                name="day"
                                                control={control}
                                                rules={{ required: 'Day is required.' }}
                                                render={({ field, fieldState }) => (
                                                    <>
                                                        <MDBoxRoot px={1} component="td">
                                                            <MDTypographyRoot variant="h5" px={1} ownerState={{ color: "dark" }}>Day
                                                            </MDTypographyRoot>
                                                        </MDBoxRoot>
                                                        <MDBoxRoot px={1} sx={{ width: "100%", textAlign: "left", padding: "14px" }} component="td" >
                                                            <Calendar
                                                                inputId={field.name}
                                                                value={field.value}
                                                                onChange={field.onChange}
                                                                dateFormat="mm/dd/yy"
                                                                inputStyle={inputStyle}
                                                                className={classNames('p-calendar', { 'input-color': !fieldState.error })} />
                                                        </MDBoxRoot>
                                                        {getFormErrorMessage(field.name)}
                                                    </>
                                                )}
                                            />
                                        </MDBoxRoot>
                                        <MDBoxRoot
                                            lineHeight={1}
                                            component="tr"
                                            sx={{ verticalAlign: "middle" }}>
                                            <Controller
                                                name="time"
                                                control={control}
                                                rules={{ required: 'Time is required.' }}
                                                render={({ field, fieldState }) => (
                                                    <>
                                                        <MDBoxRoot px={1} component="td">
                                                            <MDTypographyRoot variant="h5" px={1} ownerState={{ color: "dark" }}>Time
                                                            </MDTypographyRoot>
                                                        </MDBoxRoot>
                                                        <MDBoxRoot px={1} sx={{ width: "100%", textAlign: "left", padding: "14px" }} component="td" >
                                                            <Calendar
                                                                inputId={field.name}
                                                                value={field.value}
                                                                inputStyle={inputStyle}
                                                                onChange={field.onChange}
                                                                timeOnly
                                                                hourFormat="12"
                                                                onBlur={field.onBlur}
                                                                onInput={handleInput} // Intercepts keyboard input dynamically
                                                                readOnlyInput={false}
                                                                placeholder="hh:mm AM"
                                                                className={classNames('Mui-focused', { 'input-color': !fieldState.error })} />
                                                        </MDBoxRoot>
                                                        {getFormErrorMessage(field.name)}
                                                    </>
                                                )}
                                            />
                                        </MDBoxRoot>

                                    </MDBoxRoot>
                                </MDBoxRoot>
                            </MDBoxRoot>

                            <MDBoxRoot
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                flexDirection="row"
                                gap="16px"
                                pt={2}
                                pb={0.5}
                                px={2}
                                width="100%"
                            >
                                <MDButtonRoot
                                    ownerState={{
                                        variant: "gradient",
                                        color: "info",
                                        textTransform: "none",
                                    }}
                                    onClick={handleEmailClick}
                                >
                                    Email
                                </MDButtonRoot>
                                <MDButtonRoot
                                    ownerState={{
                                        variant: "gradient",
                                        color: "info",
                                        textTransform: "none",
                                    }}
                                    onClick={handleCreateSeriesClick}
                                >
                                    New Series
                                </MDButtonRoot>
                                <MDButtonRoot
                                    ownerState={{
                                        variant: "gradient",
                                        color: "info",
                                        textTransform: "none",
                                    }}
                                    type="submit"
                                    sx={{
                                        height: "100%",
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                    }}>
                                    Submit </MDButtonRoot>
                                <MDButtonRoot
                                    ownerState={{
                                        variant: "gradient",
                                        color: "dark",
                                        textTransform: "none",
                                    }}
                                    onClick={handleDeleteClick}>
                                    Delete</MDButtonRoot>
                            </MDBoxRoot>
                        </form>
                    ) : (
                        <form onSubmit={handleSubmit(submit, errorHandler)} id="eventForm">
                            <MDBoxRoot
                                component="main" px={2} py={4}
                                display="flex"
                                justifyContent="space-between"
                                flexDirection='row'
                                sx={{ width: '100%' }}
                            >
                                <MDBoxRoot component="table" width="100%">
                                    <MDBoxRoot component="tbody">
                                        <MDBoxRoot
                                            lineHeight={1}
                                            component="tr"
                                            sx={{ verticalAlign: "middle" }}>
                                            <MDBoxRoot px={1} component="td" >
                                                <MDTypographyRoot variant="h5" px={1} ownerState={{ color: "dark" }}>Class</MDTypographyRoot>
                                            </MDBoxRoot>
                                            <MDBoxRoot px={1} sx={{ width: "100%", textAlign: "left" }} component="td">
                                                <MDInput
                                                    {...register('class', { valueAsNumber: true, required: true })}
                                                    value={classValue}
                                                    select
                                                    label=""
                                                    variant="standard"
                                                    fullWidth
                                                    onChange={handleClassChange}>
                                                    {Cdata?.map((item) => { return <MenuItem value={item.node.id} key={item.node.id}>{item.node.name}</MenuItem> })}
                                                </MDInput>
                                                {errors.class && <MDBoxRoot><MDTypographyRoot variant="h6" px={1} ownerState={{ color: "error" }}>This field is required</MDTypographyRoot></MDBoxRoot>}
                                            </MDBoxRoot>
                                        </MDBoxRoot>
                                        <MDBoxRoot
                                            lineHeight={1}
                                            component="tr"
                                            sx={{ verticalAlign: "middle" }}>
                                            <MDBoxRoot px={1} component="td">
                                                <MDTypographyRoot variant="h5" px={1} ownerState={{ color: "secondary" }}>Capacity</MDTypographyRoot>
                                            </MDBoxRoot>
                                            <MDBoxRoot px={1} sx={{ width: "100%", textAlign: "left" }} component="td" >
                                                <MDInput
                                                    {...register('capacity', { valueAsNumber: true, required: true })}
                                                    type='number'
                                                    label=""
                                                    variant="standard"
                                                    aria-invalid={errors.capacity ? true : false} />
                                                {errors.capacity && <MDBoxRoot><MDTypographyRoot variant="h6" px={1} ownerState={{ color: "error" }}>This field is required</MDTypographyRoot></MDBoxRoot>}
                                            </MDBoxRoot>
                                        </MDBoxRoot>
                                        <MDBoxRoot
                                            lineHeight={1}
                                            component="tr"
                                            sx={{ verticalAlign: "middle" }}>
                                            <MDBoxRoot px={1} component="td">
                                                <MDTypographyRoot variant="h5" px={1} ownerState={{ color: "dark" }}>Notes</MDTypographyRoot>
                                            </MDBoxRoot>
                                            <MDBoxRoot px={1} sx={{ width: "100%", textAlign: "left" }} component="td" >
                                                <MDInput
                                                    {...register('notes')}
                                                    label=""
                                                    variant="standard"
                                                    sx={{ width: "100%", textAlign: "left" }} />
                                            </MDBoxRoot>
                                        </MDBoxRoot>
                                    </MDBoxRoot>
                                </MDBoxRoot>
                                <MDBoxRoot component="table" width="100%">
                                    <MDBoxRoot component="tbody">
                                        <MDBoxRoot
                                            lineHeight={1}
                                            component="tr"
                                            sx={{ verticalAlign: "middle" }}>
                                            <MDBoxRoot px={1} component="td">
                                                <MDTypographyRoot variant="h5"
                                                    //px={isSmallScreen ||isSMediumScreen || isLMediumScreen? 2 : 1} 
                                                    ownerState={{ color: "dark" }}

                                                >Status</MDTypographyRoot>
                                            </MDBoxRoot>
                                            <MDBoxRoot px={1} sx={{ width: "100%", textAlign: "left" }} component="td" >
                                                <MDInput
                                                    {...register('status', { valueAsNumber: true })}
                                                    value={status}
                                                    select
                                                    label=""
                                                    variant="standard"
                                                    fullWidth
                                                    onChange={handleStatusChange}
                                                >
                                                    <MenuItem value='0'>Scheduled</MenuItem>
                                                    <MenuItem value='1'>Completed</MenuItem>
                                                    <MenuItem value='2'>Canceled</MenuItem>
                                                </MDInput>
                                            </MDBoxRoot>
                                        </MDBoxRoot>
                                        <MDBoxRoot
                                            lineHeight={1}
                                            component="tr"
                                            sx={{ verticalAlign: "middle" }}>

                                            <Controller
                                                name="day"
                                                control={control}
                                                rules={{ required: 'Day is required.' }}
                                                render={({ field, fieldState }) => (
                                                    <>
                                                        <MDBoxRoot px={1} component="td">
                                                            <MDTypographyRoot variant="h5" px={1} ownerState={{ color: "dark" }}>Day
                                                            </MDTypographyRoot>
                                                        </MDBoxRoot>
                                                        <MDBoxRoot px={1} sx={{ width: "100%", textAlign: "left", padding: "14px" }} component="td" >
                                                            <Calendar
                                                                inputId={field.name} value={field.value}
                                                                onChange={field.onChange} dateFormat="mm/dd/yy"
                                                                inputStyle={inputStyle}
                                                                className={classNames('p-calendar', { 'input-color': !fieldState.error })} />
                                                        </MDBoxRoot>
                                                        {getFormErrorMessage(field.name)}
                                                    </>
                                                )}
                                            />
                                        </MDBoxRoot>
                                        <MDBoxRoot
                                            lineHeight={1}
                                            component="tr"
                                            sx={{ verticalAlign: "middle" }}>
                                            <Controller
                                                name="time"
                                                control={control}
                                                rules={{ required: 'Time is required.' }}
                                                render={({ field, fieldState }) => (
                                                    <>
                                                        <MDBoxRoot px={1} component="td">
                                                            <MDTypographyRoot variant="h5" px={1} ownerState={{ color: "dark" }}>Time
                                                            </MDTypographyRoot>
                                                        </MDBoxRoot>
                                                        <MDBoxRoot px={1} sx={{ width: "100%", textAlign: "left", padding: "14px" }} component="td" >
                                                            <Calendar
                                                                inputId={field.name}
                                                                value={field.value}
                                                                inputStyle={inputStyle}
                                                                onChange={field.onChange}
                                                                onBlur={field.onBlur}
                                                                timeOnly
                                                                hourFormat="12"
                                                                onInput={handleInput} // Intercepts keyboard input dynamically
                                                                placeholder="hh:mm AM"
                                                                className={classNames('Mui-focused', { 'input-color': !fieldState.error })} />
                                                        </MDBoxRoot>
                                                        {getFormErrorMessage(field.name)}
                                                    </>
                                                )}
                                            />
                                        </MDBoxRoot>

                                    </MDBoxRoot>
                                </MDBoxRoot>
                            </MDBoxRoot>
                            <MDBoxRoot
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                flexDirection="row"
                                gap="16px"
                                pt={2}
                                pb={0.5}
                                px={2}
                                width="100%"
                            >
                                <MDButtonRoot
                                    ownerState={{
                                        variant: "gradient",
                                        color: "info",
                                        textTransform: "none",
                                    }}
                                    onClick={handleEmailClick}
                                    endIcon={<EmailIcon />}
                                >
                                    Email Attendees
                                </MDButtonRoot>
                                <MDButtonRoot
                                    ownerState={{
                                        variant: "gradient",
                                        color: "info",
                                        textTransform: "none",
                                    }}
                                    onClick={handleCreateSeriesClick}
                                    endIcon={<DynamicFeedIcon />}
                                >
                                    Create Series
                                </MDButtonRoot>
                                <MDButtonRoot
                                    ownerState={{
                                        variant: "gradient",
                                        color: "info",
                                        textTransform: "none",
                                    }}
                                    endIcon={<SaveIcon />}
                                    type="submit">
                                    Submit </MDButtonRoot>
                                <MDButtonRoot
                                    ownerState={{
                                        variant: "gradient",
                                        color: "dark",
                                        textTransform: "none",
                                    }}
                                    onClick={handleDeleteClick}
                                    endIcon={<DeleteIcon />}>
                                    Delete</MDButtonRoot>
                            </MDBoxRoot>
                        </form>
                    )
                    }
                </MDBoxRoot>

                <CreateSeriesDialog open={isSeriesOpen} onClose={() => { setIsSeriesOpen(false) }} onSave={handleSeriesSave} date={expandedEvent.date} />

                {isDeleteDialogOpen && (<DeleteEventDialog attendies={attendies} name={eventName} handleDeleteEvent={handleDeleteEvent} onClose={() => setIsDeleteDialogOpen(false)} />)}
            </Card>
        </MDBoxRoot>
    )
}


export default EditEventDialog;

interface DeleteEventDialogProps {
    attendies: types.Fs_SignupEdge[];
    name: string;
    handleDeleteEvent: () => void;
    onClose: () => void;
}
const DeleteEventDialog: FC<DeleteEventDialogProps> = ({ attendies, name, handleDeleteEvent, onClose }) => {

    const attendiesCount = attendies.length;

    return (
        <Dialog
            maxWidth="sm"
            open={true}
            fullWidth
        >
            <MDTypographyRoot variant="h5" px={1} py={1}>Delete class? <CloseIcon onClick={onClose} sx={{ position: "absolute", top: "10px", right: "10px" }} /></MDTypographyRoot>
            <DialogContent dividers>
                <MDTypographyRoot variant="h4" px={1} ownerState={{ color: "dark" }}>
                    {attendiesCount === 0 && `Deleting event "${name}"`}
                    {attendiesCount !== 0 && `Event "${name}" has ${attendiesCount} attendies. Remove attendies before deleting the event.`}
                </MDTypographyRoot>
            </DialogContent>
            <DialogActions>
                {attendiesCount === 0 && (
                    <MDButtonRoot
                        ownerState={{
                            variant: "gradient",
                            color: "info",
                            textTransform: "none",
                            circular: false,
                            iconOnly: false,
                            darkMode: false
                        }}
                        size="medium"
                        onClick={handleDeleteEvent}
                        type="submit">
                        Delete
                    </MDButtonRoot>
                )}
                <MDButtonRoot
                    size="medium"
                    onClick={onClose}
                    ownerState={{
                        textTransform: "none",
                        variant: "gradient",
                        color: "warning",
                    }}>
                    Cancel
                </MDButtonRoot>
            </DialogActions>
        </Dialog>);
}
